/* ==================================================================
 * NERO B2B — Catalog cart + MOQ + filters
 * Path: assets/b2b-catalog.js
 * ------------------------------------------------------------------
 * Handles the B2B catalog page interactions:
 *   1. Product card size + quantity selectors
 *   2. Add to Shopify cart via /cart/add.js
 *   3. Sticky cart sidebar rendering from /cart.js
 *   4. MOQ progress bar (default 10 pieces)
 *   5. Dual pricing display (public crossed / wholesale live)
 *   6. Filter chips (client-side filter by data-filter-category)
 *   7. Checkout redirect to /discount/{code}?redirect=/checkout
 *
 * The discount is applied at checkout via Shopify's discount URL
 * mechanism, so the live Shopify cart shows the PUBLIC price but the
 * customer sees the discounted total in the B2B sidebar (estimate).
 * ================================================================== */

(function () {
  'use strict';

  var catalog = document.querySelector('[data-nero-b2b-catalog]');
  if (!catalog) return;

  var moneyFormat = (window.Shopify && window.Shopify.money_format) || '€ {{amount}}';
  var cartRoot = document.querySelector('[data-b2b-cart]');
  var cartLines = document.querySelector('[data-cart-lines]');
  var cartEmpty = document.querySelector('[data-cart-empty]');
  var subtotalPublicEl = document.querySelector('[data-subtotal-public]');
  var subtotalDiscountEl = document.querySelector('[data-subtotal-discount]');
  var subtotalFinalEl = document.querySelector('[data-subtotal-final]');
  var checkoutBtn = document.querySelector('[data-checkout-btn]');
  var moqWidget = document.querySelector('[data-moq-widget]');
  var moqCurrent = document.querySelector('[data-moq-current]');
  var moqFill = document.querySelector('[data-moq-fill]');
  var moqStatus = document.querySelector('[data-moq-status]');
  var filtersWrap = document.querySelector('[data-b2b-filters]');
  var grid = document.querySelector('[data-b2b-grid]');
  var discountBtnCopy = document.querySelector('[data-copy-code]');
  var discountCodeEl = document.querySelector('[data-discount-code]');

  var MOQ_TARGET = moqWidget ? parseInt(moqWidget.dataset.moqTarget || '10', 10) : 10;
  var DISCOUNT_CODE = checkoutBtn ? (checkoutBtn.dataset.discountCode || '').trim() : '';

  // Read discount percent from an inline data-attr stored on a marker
  // We compute it from b2b-card pricing: pub vs b2b prices. Default 40.
  var DISCOUNT_PERCENT = 40;

  /* ---- Utils ---- */

  function formatMoney(cents) {
    // Shopify money_format can contain complex placeholders — we use a
    // lightweight Italian-style fallback.
    var v = (cents / 100).toFixed(2).replace('.', ',');
    return '€ ' + v.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function parsePriceAttr(v) {
    // Shopify price attributes are in cents (integer) or decimal cents
    // depending on context. product.price in Liquid → cents (int).
    // product.price | money → formatted string. We use data-product-price
    // which is the raw cents integer from Liquid product.price.
    if (!v) return 0;
    var n = parseInt(v, 10);
    if (isNaN(n)) return 0;
    return n;
  }

  /* ---- Size chip selection ---- */

  document.querySelectorAll('.b2b-card').forEach(function (card) {
    var sizeBtns = card.querySelectorAll('.b2b-size');
    if (!sizeBtns.length) return;

    sizeBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return;
        sizeBtns.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-checked', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-checked', 'true');

        // Update the add-to-cart button's variant id
        var addBtn = card.querySelector('[data-add-to-cart]');
        if (addBtn && btn.dataset.variantId) {
          addBtn.dataset.variantId = btn.dataset.variantId;
        }
      });
    });

    // Auto-select first non-disabled size
    var firstAvailable = null;
    for (var i = 0; i < sizeBtns.length; i++) {
      if (!sizeBtns[i].disabled) { firstAvailable = sizeBtns[i]; break; }
    }
    if (firstAvailable) {
      firstAvailable.classList.add('is-active');
      firstAvailable.setAttribute('aria-checked', 'true');
      var addBtn = card.querySelector('[data-add-to-cart]');
      if (addBtn && firstAvailable.dataset.variantId) {
        addBtn.dataset.variantId = firstAvailable.dataset.variantId;
      }
    }
  });

  /* ---- Qty +/- ---- */

  document.querySelectorAll('.b2b-qty').forEach(function (qtyWrap) {
    var input = qtyWrap.querySelector('[data-qty-input]');
    var dec = qtyWrap.querySelector('[data-qty-decrement]');
    var inc = qtyWrap.querySelector('[data-qty-increment]');
    if (!input) return;

    function clamp(v) {
      v = parseInt(v, 10) || 1;
      if (v < 1) v = 1;
      if (v > 99) v = 99;
      return v;
    }

    if (dec) dec.addEventListener('click', function () { input.value = clamp(parseInt(input.value, 10) - 1); });
    if (inc) inc.addEventListener('click', function () { input.value = clamp(parseInt(input.value, 10) + 1); });
    input.addEventListener('change', function () { input.value = clamp(input.value); });
  });

  /* ---- Add to cart ---- */

  document.querySelectorAll('[data-add-to-cart]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var card = btn.closest('.b2b-card');
      var variantId = btn.dataset.variantId;
      var qtyInput = card && card.querySelector('[data-qty-input]');
      var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

      if (!variantId) {
        console.warn('[NeRò B2B] nessun variant id sul pulsante add-to-cart');
        return;
      }

      btn.disabled = true;
      var originalText = btn.innerHTML;
      btn.innerHTML = 'Aggiungo…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: qty })
      })
      .then(function (r) { return r.json(); })
      .then(function () {
        btn.innerHTML = 'Aggiunto ✓';
        setTimeout(function () { btn.innerHTML = originalText; btn.disabled = false; }, 1200);
        return refreshCart();
      })
      .catch(function (err) {
        console.error('[NeRò B2B] add-to-cart fallito:', err);
        btn.innerHTML = 'Errore';
        setTimeout(function () { btn.innerHTML = originalText; btn.disabled = false; }, 1500);
      });
    });
  });

  /* ---- Cart rendering ---- */

  function refreshCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderCart(cart); })
      .catch(function (err) {
        console.error('[NeRò B2B] /cart.js fetch fallito:', err);
      });
  }

  function renderCart(cart) {
    if (!cartLines) return;
    var items = cart.items || [];
    var totalQty = cart.item_count || 0;
    var totalPriceCents = cart.items_subtotal_price || cart.total_price || 0;

    // Clear existing (except empty state)
    cartLines.innerHTML = '';

    if (!items.length) {
      var empty = document.createElement('li');
      empty.className = 'b2b-cart__empty';
      empty.setAttribute('data-cart-empty', '');
      empty.innerHTML = '<p>Nessun articolo ancora. Scegli taglia e aggiungi dai prodotti qui accanto.</p>';
      cartLines.appendChild(empty);
    } else {
      items.forEach(function (item) {
        var li = document.createElement('li');
        li.className = 'b2b-cart__line';
        li.dataset.lineKey = item.key;
        li.innerHTML = [
          '<div class="b2b-cart__line-body">',
          '  <strong class="b2b-cart__line-title">' + escapeHtml(item.product_title) + '</strong>',
          '  <span class="b2b-cart__line-meta">' + escapeHtml(item.variant_title || '') + ' · q.tà ' + item.quantity + '</span>',
          '</div>',
          '<div class="b2b-cart__line-price">' + formatMoney(item.line_price) + '</div>',
          '<button type="button" class="b2b-cart__line-remove" data-line-key="' + item.key + '" aria-label="Rimuovi ' + escapeHtml(item.product_title) + '">×</button>'
        ].join('');
        cartLines.appendChild(li);
      });

      cartLines.querySelectorAll('.b2b-cart__line-remove').forEach(function (rm) {
        rm.addEventListener('click', function () {
          var key = rm.dataset.lineKey;
          removeLine(key);
        });
      });
    }

    updateMOQ(totalQty);
    updateTotals(totalPriceCents);
    updateCheckoutButton(totalQty);
  }

  function removeLine(key) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    }).then(function () { return refreshCart(); });
  }

  function updateMOQ(totalQty) {
    if (moqCurrent) moqCurrent.textContent = totalQty;
    if (moqFill) {
      var pct = Math.min(100, Math.round((totalQty / MOQ_TARGET) * 100));
      moqFill.style.width = pct + '%';
    }
    if (moqStatus) {
      if (totalQty >= MOQ_TARGET) {
        moqStatus.textContent = 'MOQ raggiunto — puoi ordinare.';
        moqStatus.classList.add('is-reached');
      } else {
        var remaining = MOQ_TARGET - totalQty;
        moqStatus.textContent = 'Aggiungi altri ' + remaining + ' pezz' + (remaining === 1 ? 'o' : 'i') + ' per ordinare';
        moqStatus.classList.remove('is-reached');
      }
    }
  }

  function updateTotals(publicCents) {
    var discountAmount = Math.round(publicCents * DISCOUNT_PERCENT / 100);
    var finalCents = publicCents - discountAmount;
    if (subtotalPublicEl) subtotalPublicEl.textContent = formatMoney(publicCents);
    if (subtotalDiscountEl) subtotalDiscountEl.textContent = '−' + formatMoney(discountAmount);
    if (subtotalFinalEl) subtotalFinalEl.textContent = formatMoney(finalCents);
  }

  function updateCheckoutButton(totalQty) {
    if (!checkoutBtn) return;
    var canCheckout = totalQty >= MOQ_TARGET;
    checkoutBtn.disabled = !canCheckout;
    if (canCheckout) checkoutBtn.classList.remove('is-disabled');
    else checkoutBtn.classList.add('is-disabled');
  }

  /* ---- Checkout: apply discount code and go to /checkout ---- */

  if (checkoutBtn) {
    checkoutBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (checkoutBtn.disabled) return;

      if (DISCOUNT_CODE) {
        // Shopify discount URL flow applies the code and redirects to checkout
        window.location.href = '/discount/' + encodeURIComponent(DISCOUNT_CODE) + '?redirect=/checkout';
      } else {
        window.location.href = '/checkout';
      }
    });
  }

  /* ---- Filter chips ---- */

  if (filtersWrap && grid) {
    filtersWrap.addEventListener('click', function (e) {
      var btn = e.target.closest('.b2b-filter');
      if (!btn) return;
      var key = btn.dataset.filter || 'all';

      filtersWrap.querySelectorAll('.b2b-filter').forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');

      grid.querySelectorAll('.b2b-card').forEach(function (card) {
        var cat = card.dataset.filterCategory || 'all';
        if (key === 'all' || cat === key) {
          card.style.display = '';
        } else {
          card.style.display = 'none';
        }
      });
    });
  }

  /* ---- Copy discount code ---- */

  if (discountBtnCopy && discountCodeEl) {
    discountBtnCopy.addEventListener('click', function () {
      var code = discountCodeEl.textContent.trim();
      var defaultLabel = discountBtnCopy.querySelector('[data-copy-default]');
      var doneLabel = discountBtnCopy.querySelector('[data-copy-done]');

      var done = function () {
        if (defaultLabel) defaultLabel.hidden = true;
        if (doneLabel) doneLabel.hidden = false;
        setTimeout(function () {
          if (defaultLabel) defaultLabel.hidden = false;
          if (doneLabel) doneLabel.hidden = true;
        }, 1800);
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(code).then(done).catch(function () {
          fallbackCopy(code);
          done();
        });
      } else {
        fallbackCopy(code);
        done();
      }
    });
  }

  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ---- Initial cart load ---- */
  refreshCart();
})();
